import { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'react-toastify';
import { useConfirm } from 'material-ui-confirm';
import { v4 as uuidv4 } from 'uuid';

import {
  Box,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  Chip,
} from '@mui/material';

// Icons
import AddIcon from '@mui/icons-material/Add';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import EditIcon from '@mui/icons-material/Edit';

// Utils
import messages from '../../../static/messages';

// Services
import proposalsServices from '../../../services/proposalsServices';
import workDescriptionServices from '../../../services/workDescriptionServices';

// Drag and Drop
import { SortableContainer, SortableElement, SortableHandle } from 'react-sortable-hoc';
import { arrayMoveImmutable } from 'array-move';

// Auto-save
import useAutoSave from '../../../hooks/useAutoSave';
import AutoSaveIndicator from '../../AutoSaveIndicator/AutoSaveIndicator';

// DnD Components
const DragHandle = SortableHandle(() => (
  <DragIndicatorIcon sx={{ cursor: 'grab', color: '#999', '&:hover': { color: '#333' } }} />
));

const SortableTableRow = SortableElement(({ children, ...props }) => (
  <TableRow {...props}>{children}</TableRow>
));

const SortableTableBody = SortableContainer(({ children }) => (
  <TableBody>{children}</TableBody>
));

const ProposalWorkDescriptionSection = ({ proposal, id, refresh }) => {
  const [workDescriptions, setWorkDescriptions] = useState([]);
  const [catalogList, setCatalogList] = useState([]);
  const [addMode, setAddMode] = useState(null); // null, 'catalog', 'custom'
  const [selectedItem, setSelectedItem] = useState('');
  const [customItem, setCustomItem] = useState({ description: '', price: '' });
  const [editingId, setEditingId] = useState(null);
  const [editingData, setEditingData] = useState({ description: '', price: '' });
  const dirtyRef = useRef(false);

  const confirm = useConfirm();

  // Auto-save function
  const saveWorkDescriptions = useCallback(async (data) => {
    const response = await proposalsServices.update(id, { workDescriptions: data });
    dirtyRef.current = false;
    if (response.status === 200 && refresh) {
      refresh();
    }
  }, [id, refresh]);

  // Save immediately on user actions (add/edit/delete/reorder) — no timer
  const {
    isSaving,
    hasUnsavedChanges,
    lastSaved,
    error,
    saveNow,
    getLastSavedText,
  } = useAutoSave(saveWorkDescriptions);

  const saveNowSafe = useCallback((data) => {
    dirtyRef.current = true;
    saveNow(data).catch(() => {
      toast.error('Auto-save failed. Please retry the action.');
    });
  }, [saveNow]);

  // Load catalog items
  const loadCatalogList = async () => {
    try {
      const response = await workDescriptionServices.getAll(0, 1000000, false);
      if (response.status === 206 || response.status === 200) {
        setCatalogList(response.data.data || []);
      }
    } catch (error) {
      console.log(error);
    }
  };

  // Add from catalog
  const handleAddFromCatalog = () => {
    if (!selectedItem) {
      toast.error('Please select an item');
      return;
    }

    const item = catalogList.find((w) => w.id === selectedItem);
    if (!item) return;

    const newItem = {
      id: `catalog-${item.id}-${Date.now()}`,
      description: item.description,
      price: Number(item.price) || 0,
      isCustom: false,
    };

    const updated = [...workDescriptions, newItem];
    setWorkDescriptions(updated);
    saveNowSafe(updated);
    setAddMode(null);
    setSelectedItem('');
  };

  // Add custom item
  const handleAddCustom = () => {
    if (!customItem.description.trim()) {
      toast.error('Please enter a description');
      return;
    }

    const newItem = {
      id: `custom-${uuidv4()}`,
      description: customItem.description.trim(),
      price: Number(customItem.price) || 0,
      isCustom: true,
    };

    const updated = [...workDescriptions, newItem];
    setWorkDescriptions(updated);
    saveNowSafe(updated);
    setAddMode(null);
    setCustomItem({ description: '', price: '' });
  };

  // Delete item
  const handleDelete = (itemId) => {
    confirm({
      title: messages.confirm_message.remove,
      description: messages.confirm_message.remove_description('this work description'),
    })
      .then(() => {
        const updated = workDescriptions.filter((w) => w.id !== itemId);
        setWorkDescriptions(updated);
        saveNowSafe(updated);
      })
      .catch(() => toast.info(messages.cancel_message));
  };

  // Start editing
  const handleStartEdit = (item) => {
    setEditingId(item.id);
    setEditingData({ description: item.description, price: item.price || '' });
  };

  // Cancel editing
  const handleCancelEdit = () => {
    setEditingId(null);
    setEditingData({ description: '', price: '' });
  };

  // Save editing
  const handleSaveEdit = () => {
    if (!editingData.description.trim()) {
      toast.error('Description cannot be empty');
      return;
    }

    const updated = workDescriptions.map((w) =>
      w.id === editingId
        ? { ...w, description: editingData.description.trim(), price: Number(editingData.price) || 0 }
        : w
    );
    setWorkDescriptions(updated);
    saveNowSafe(updated);
    setEditingId(null);
    setEditingData({ description: '', price: '' });
  };

  // Handle drag and drop reorder
  const handleSortEnd = ({ oldIndex, newIndex }) => {
    if (oldIndex === newIndex) return;
    const updated = arrayMoveImmutable(workDescriptions, oldIndex, newIndex);
    setWorkDescriptions(updated);
    saveNowSafe(updated);
  };

  // Calculate total
  const totalPrice = workDescriptions.reduce((sum, w) => sum + (Number(w.price) || 0), 0);

  // Hooks
  useEffect(() => {
    loadCatalogList();
  }, []);

  useEffect(() => {
    if (proposal?.workDescriptions && !dirtyRef.current) {
      setWorkDescriptions(proposal.workDescriptions);
    }
  }, [proposal]);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">
          Work Description
          <Typography component="span" sx={{ ml: 2, color: 'primary.main', fontWeight: 600 }}>
            Total: ${totalPrice.toFixed(2)}
          </Typography>
        </Typography>
        <AutoSaveIndicator
          isSaving={isSaving}
          hasUnsavedChanges={hasUnsavedChanges}
          lastSaved={lastSaved}
          error={error}
          getLastSavedText={getLastSavedText}
          variant="chip"
        />
      </Box>

      {workDescriptions.length === 0 ? (
        <Typography color="text.secondary" sx={{ fontStyle: 'italic' }}>
          No work descriptions added. Click "From Catalog" to select from the catalog or "Custom Item" to add your own.
        </Typography>
      ) : (
        <>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell width={40}></TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell align="center" width={80}>Type</TableCell>
                  <TableCell align="right" width={120}>Price</TableCell>
                  <TableCell align="center" width={60}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <SortableTableBody useDragHandle onSortEnd={handleSortEnd} helperClass="sortable-row-helper">
                {workDescriptions.map((item, index) => (
                  <SortableTableRow key={item.id} index={index}>
                    <TableCell width={40}>
                      <DragHandle />
                    </TableCell>
                    {editingId === item.id ? (
                      <>
                        <TableCell>
                          <TextField
                            size="small"
                            fullWidth
                            multiline
                            maxRows={4}
                            value={editingData.description}
                            onChange={(e) => setEditingData({ ...editingData, description: e.target.value })}
                            autoFocus
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={item.isCustom ? 'Custom' : 'Catalog'}
                            size="small"
                            color={item.isCustom ? 'warning' : 'default'}
                            sx={{ fontSize: '0.7rem' }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <TextField
                            size="small"
                            type="number"
                            sx={{ width: 100 }}
                            value={editingData.price}
                            onChange={(e) => setEditingData({ ...editingData, price: e.target.value })}
                            InputProps={{
                              startAdornment: <InputAdornment position="start">$</InputAdornment>,
                            }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Box sx={{ display: 'flex', gap: 0.25 }}>
                            <Tooltip title="Save">
                              <IconButton size="small" onClick={handleSaveEdit} color="primary">
                                <SaveIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Cancel">
                              <IconButton size="small" onClick={handleCancelEdit}>
                                <CancelIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </>
                    ) : (
                      <>
                        <TableCell>{item.description}</TableCell>
                        <TableCell align="center">
                          <Chip
                            label={item.isCustom ? 'Custom' : 'Catalog'}
                            size="small"
                            color={item.isCustom ? 'warning' : 'default'}
                            sx={{ fontSize: '0.7rem' }}
                          />
                        </TableCell>
                        <TableCell align="right">${parseFloat(item.price || 0).toFixed(2)}</TableCell>
                        <TableCell align="center">
                          <Box sx={{ display: 'flex', gap: 0.25 }}>
                            <Tooltip title="Edit">
                              <IconButton size="small" onClick={() => handleStartEdit(item)} color="primary">
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton size="small" onClick={() => handleDelete(item.id)} color="error">
                                <DeleteForeverIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </>
                    )}
                  </SortableTableRow>
                ))}
              </SortableTableBody>
            </Table>
          </TableContainer>

          {/* Total Row */}
          <Box sx={{ mt: 1, p: 1, backgroundColor: '#fff3e0', borderRadius: 1, textAlign: 'right' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Work Description Total: ${totalPrice.toFixed(2)}
            </Typography>
          </Box>
        </>
      )}

      {/* Add from catalog - below list */}
      {addMode === 'catalog' && (
        <Box sx={{ mt: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1, backgroundColor: '#f9f9f9' }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Add from Catalog</Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 300, md: 400 }, flex: { xs: '1 1 100%', sm: '0 1 auto' } }}>
              <InputLabel>Select Work Description</InputLabel>
              <Select
                value={selectedItem}
                onChange={(e) => setSelectedItem(e.target.value)}
                label="Select Work Description"
              >
                <MenuItem value="">Select from list</MenuItem>
                {catalogList.map((w) => (
                  <MenuItem key={w.id} value={w.id}>
                    {w.description} - ${Number(w.price).toFixed(2)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', width: { xs: '100%', sm: 'auto' }, gap: 1 }}>
              <IconButton size="small" color="primary" onClick={handleAddFromCatalog} title="Save">
                <SaveIcon />
              </IconButton>
              <IconButton size="small" onClick={() => { setAddMode(null); setSelectedItem(''); }} title="Cancel">
                <CancelIcon />
              </IconButton>
            </Box>
          </Box>
        </Box>
      )}

      {/* Add custom - below list */}
      {addMode === 'custom' && (
        <Box sx={{ mt: 2, p: 2, border: '1px solid #ed6c02', borderRadius: 1, backgroundColor: '#fff8e1' }}>
          <Typography variant="subtitle2" sx={{ mb: 1, color: '#ed6c02' }}>Add Custom Item</Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <TextField
              label="Description"
              size="small"
              multiline
              rows={2}
              sx={{ minWidth: { xs: '100%', sm: 300, md: 400 }, flex: { xs: '1 1 100%', sm: '0 1 auto' } }}
              value={customItem.description}
              onChange={(e) => setCustomItem({ ...customItem, description: e.target.value })}
              placeholder="Enter custom work description..."
            />
            <TextField
              label="Price"
              size="small"
              type="number"
              sx={{ width: { xs: '100%', sm: 120 } }}
              value={customItem.price}
              onChange={(e) => setCustomItem({ ...customItem, price: e.target.value })}
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
              }}
            />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', width: { xs: '100%', sm: 'auto' }, gap: 1 }}>
              <IconButton size="small" color="primary" onClick={handleAddCustom} title="Save">
                <SaveIcon />
              </IconButton>
              <IconButton size="small" onClick={() => { setAddMode(null); setCustomItem({ description: '', price: '' }); }} title="Cancel">
                <CancelIcon />
              </IconButton>
            </Box>
          </Box>
        </Box>
      )}

      {/* Add buttons at bottom left */}
      {!addMode && (
        <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => setAddMode('catalog')}
          >
            From Catalog
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<EditIcon />}
            onClick={() => setAddMode('custom')}
          >
            Custom Item
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default ProposalWorkDescriptionSection;
